export const routes = {
  home: { href: '/me' },
  login: {
    href: '/login',
    fmt: (email: string): string => `/login?email=${encodeURIComponent(email)}`,
  },
  autoLogin: { href: '/auto-login' },
  signup: { href: '/signup' },
  invitationSignUp: {
    href: '/invite',
    fmt: (email: string, token: string): string =>
      `/invite?email=${encodeURIComponent(email)}&token=${token}`,
  },
  me: {
    href: '/me',
    documents: { href: '/documents', full: '/me/documents' },
    settings: { href: '/me/settings' },
    subscription: {
      href: '/subscription/:subscriptionId',
      fmt: (subscriptionId: string): string => `/me/subscription/${subscriptionId}`,
    },
  },
  admin: {
    href: '/manage',
    graphql: { href: '/graphql', full: '/manage/graphql' },
    individuals: {
      href: '/individuals',
      full: '/manage/individuals',
    },
    individual: {
      href: '/individual/:individualIdentityId',
      full: '/manage/individual/:individualIdentityId',
      settings: {
        href: 'settings',
        full: '/manage/individual/:individualIdentityId/settings',
        fmt: (individualIdentityId: string): string =>
          `/manage/individual/${individualIdentityId}/settings`,
      },
      documents: {
        href: 'documents',
        full: '/manage/individual/:individualIdentityId/documents',
        fmt: (individualIdentityId: string): string =>
          `/manage/individual/${individualIdentityId}/documents`,
      },
    },
    companies: {
      href: '/companies',
      full: '/manage/companies',
    },
    company: {
      href: '/company/:companyId',
      full: '/manage/company/:companyId',
      fmt: (companyId: string): string => `/manage/company/${companyId}`,
      settings: {
        href: 'settings',
        full: '/manage/company/:companyId/settings',
        fmt: (companyId: string): string => `/manage/company/${companyId}/settings`,
      },
      documents: {
        href: 'documents',
        full: '/manage/company/:companyId/documents',
        fmt: (companyId: string): string => `/manage/company/${companyId}/documents`,
      },
      captable: {
        href: 'captable',
        full: '/manage/company/:companyId/captable',
        fmt: (companyId: string): string => `/manage/company/${companyId}/captable`,
      },
      fundraising: {
        href: 'fundraising/:fundraisingId',
        full: '/manage/company/:companyId/fundraising/:fundraisingId',
        fmt: (companyId: string, fundraisingId: string): string =>
          `/manage/company/${companyId}/fundraising/${fundraisingId}`,
      },
    },
    fundraisings: {
      href: '/fundraisings',
      full: '/manage/fundraisings',
    },
  },
  workspace: {
    href: '/workspace/:companyId',
    fmt: (companyId: string): string => `/workspace/${companyId}`,
    settings: {
      href: '/settings',
      fmt: (id: string): string => `/workspace/${id}/settings`,
    },
    documents: {
      href: '/documents',
      fmt: (id: string): string => `/workspace/${id}/documents`,
    },
    captable: {
      href: '/captable',
      fmt: (companyId: string): string => `/workspace/${companyId}/captable`,
    },
    subscription: {
      href: '/subscription/:subscriptionId',
      fmt: (companyId: string, subscriptionId: string): string =>
        `/workspace/${companyId}/subscription/${subscriptionId}`,
    },
    fundraising: {
      private: {
        create: {
          href: '/fundraising/private/create',
          full: '/workspace/:companyId/fundraising/private/create',
          fmt: (id: string): string => `/workspace/${id}/fundraising/private/create`,
        },
        parameters: {
          href: '/fundraising/private/:fundraisingId/parameters',
          full: 'workspace/:companyId/fundraising/private/:fundraisingId/parameters',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/private/${fundraisingId}/parameters`,
        },
        documentation: {
          href: '/fundraising/private/:fundraisingId/documentation',
          full: 'workspace/:companyId/fundraising/private/:fundraisingId/documentation',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/private/${fundraisingId}/documentation`,
        },
        iban: {
          href: '/fundraising/private/:fundraisingId/iban',
          full: 'workspace/:companyId/fundraising/private/:fundraisingId/iban',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/private/${fundraisingId}/iban`,
        },
        subscribers: {
          href: '/fundraising/private/:fundraisingId/subscribers',
          full: 'workspace/:companyId/fundraising/private/:fundraisingId/subscribers',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/private/${fundraisingId}/subscribers`,
        },
        summary: {
          href: '/fundraising/private/:fundraisingId/summary',
          full: 'workspace/:companyId/fundraising/private/:fundraisingId/summary',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/private/${fundraisingId}/summary`,
        },
      },
      crowdfunding: {
        create: {
          href: '/fundraising/crowdfunding/create',
          full: '/workspace/:companyId/fundraising/crowdfunding/create',
          fmt: (id: string): string => `/workspace/${id}/fundraising/crowdfunding/create`,
        },
        parameters: {
          href: '/fundraising/crowdfunding/:fundraisingId/parameters',
          full: 'workspace/:companyId/fundraising/crowdfunding/:fundraisingId/parameters',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/crowdfunding/${fundraisingId}/parameters`,
        },
        documentation: {
          href: '/fundraising/crowdfunding/:fundraisingId/documentation',
          full: 'workspace/:companyId/fundraising/crowdfunding/:fundraisingId/documentation',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/crowdfunding/${fundraisingId}/documentation`,
        },
        iban: {
          href: '/fundraising/crowdfunding/:fundraisingId/iban',
          full: 'workspace/:companyId/fundraising/crowdfunding/:fundraisingId/iban',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/crowdfunding/${fundraisingId}/iban`,
        },
        summary: {
          href: '/fundraising/crowdfunding/:fundraisingId/summary',
          full: 'workspace/:companyId/fundraising/crowdfunding/:fundraisingId/summary',
          fmt: (companyId: string, fundraisingId: string): string =>
            `/workspace/${companyId}/fundraising/crowdfunding/${fundraisingId}/summary`,
        },
      },
      href: '/fundraising/:fundraisingId',
      fmt: (companyId: string, fundraisingId: string): string =>
        `/workspace/${companyId}/fundraising/${fundraisingId}`,
      edit: {
        href: '/fundraising/:fundraisingId/edit',
        fmt: (companyId: string, fundraisingId: string): string =>
          `/workspace/${companyId}/fundraising/${fundraisingId}/edit`,
      },
    },
    option: {
      href: '/option/:assetId',
      fmt: (companyId: string, contractAddress: string): string =>
        `/workspace/${companyId}/option/${contractAddress}`,
    },
  },
  onboardings: {
    physicalIdentity: { href: '/onboarding/physicalidentity' },
    spv: {
      href: '/new-workspace/spv/:id',
      fmt: (id: string): string => `/new-workspace/spv/${id}`,
    },
    spvCreate: { href: '/new-workspace/spv/create' },
    spvEdit: {
      href: '/new-workspace/spv/:id/edit',
      fmt: (id: string): string => `/new-workspace/spv/${id}/edit`,
    },
    company: {
      create: {
        href: '/new-workspace/company/create',
      },
      infos: {
        href: '/new-workspace/company/:companyId/infos',
        fmt: (companyId: string): string => `/new-workspace/company/${companyId}/infos`,
      },
      representatives: {
        href: '/new-workspace/company/:companyId/representatives',
        fmt: (companyId: string): string => `/new-workspace/company/${companyId}/representatives`,
      },
      access: {
        href: '/new-workspace/company/:companyId/access',
        fmt: (companyId: string): string => `/new-workspace/company/${companyId}/access`,
      },
      summary: {
        href: '/new-workspace/company/:companyId/summary',
        fmt: (companyId: string): string => `/new-workspace/company/${companyId}/summary`,
      },
      owners: {
        href: '/new-workspace/company/:companyId/owners',
        fmt: (companyId: string): string => `/new-workspace/company/${companyId}/owners`,
      },
    },
  },
  verifyEmail: { href: '/verify-email' },
  onboarding: { href: '/onboarding' },
  forgotPassword: { href: '/forgot-password' },
  resetPassword: { href: '/reset-password' },
};
